import type moment from 'moment'
import React from 'react'
import { useIntl } from 'react-intl'

import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import { DayBadge } from 'tripPlanner/components/Itinerary/DatesModal'
import { getDayColor } from 'tripPlanner/utils/tripItemPin'

interface Props {
  triggerRef: React.RefObject<HTMLButtonElement | null>
  isOpen: boolean
  onClose: () => void
  minDate: moment.Moment
  maxDate: moment.Moment
  selectedDate?: moment.Moment
  onSelectDate: (date: moment.Moment) => void
  onSelectRemoveDate: () => void
  portaled?: boolean
  placement?: 'top' | 'bottom'
}

function DatesDropdown(props: Props) {
  const {
    triggerRef,
    isOpen,
    onClose,
    minDate,
    maxDate,
    selectedDate,
    onSelectDate,
    onSelectRemoveDate,
    portaled,
    placement = 'bottom',
  } = props
  const intl = useIntl()

  const days = maxDate.diff(minDate, 'days') + 1
  const clonedMinDate = minDate.clone().startOf('day')

  return (
    <DropdownList
      size="S"
      triggerRef={triggerRef}
      anchorRef={triggerRef}
      open={isOpen}
      onClose={onClose}
      placement={placement === 'top' ? 'top-end' : 'bottom-end'}
      portaled={portaled}
    >
      {selectedDate && (
        <ClickableListItem
          size="medium"
          onClick={onSelectRemoveDate}
          startIcon={<SolidHeartIcon colour="favourite" />}
          title={intl.formatMessage({
            id: 'OEd7w5fMheIBVxaF3AyR',
            defaultMessage: 'Move to Saved',
            description: 'Option to move a trip item back to saved items from itinerary',
          })}
        />
      )}
      {[...Array(days).keys()].map((i) => {
        const currentDate = clonedMinDate.clone().add(i, 'day')
        const isSelected = selectedDate?.isSame(currentDate, 'day')
        const pinColor = getDayColor(currentDate)
        return (
          <ClickableListItem
            key={currentDate.unix()}
            size="medium"
            onClick={() =>
              isSelected ? onSelectRemoveDate() : onSelectDate(currentDate)}
            selected={isSelected}
            startIcon={<DayBadge
              variant="medium"
              weight="bold"
              colour="neutral-eight"
              format="uppercase"
              style={{ backgroundColor: pinColor }}
            >
              {currentDate.format('ddd')}
            </DayBadge>}
            title={currentDate.format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}
            endIcon={isSelected ? <LineCheckIcon /> : undefined}
          />
        )
      })}
    </DropdownList>
  )
}

export default React.memo(DatesDropdown)
